import type React from 'react';
import { useEffect, useCallback, useMemo } from 'react';
import {
  Text,
  ToggleButton,
  Button,
  Badge,
  mergeClasses,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  PersonRegular,
  ArrowClockwiseRegular,
  SettingsRegular,
  BugRegular,
  MoreHorizontalRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useIncidents, useTick, useSettings, useIsMobile } from 'hooks';
import { useAuthContext, useStatusPanelMode, useToast } from 'contexts';
import { IncidentState, IncidentSeverity } from 'types';
import type { StatusPanelSettings, Incident } from 'types';
import { AppRole } from 'types/auth';
import { ConnectionStatus, IncidentTable, WallScreenIncidentList, SettingsDialog, UnassignedIncidentsBar, LiveIndicator } from 'components/StatusPanel';
import { useStatusPanelPageStyles } from './StatusPanelPage.styles';
import { SOC_COLORS } from 'themes';
import { apiClient } from 'services';

const getSeverityIntent = (severity: IncidentSeverity): 'error' | 'warning' | 'info' => {
  switch (severity) {
    case IncidentSeverity.High:
      return 'error';
    case IncidentSeverity.Medium:
      return 'warning';
    default:
      return 'info';
  }
};

export const StatusPanelPage: React.FC = () => {
  const styles = useStatusPanelPageStyles();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { isWallScreen, toggleMode } = useStatusPanelMode();
  const { showToast } = useToast();
  const now = useTick(1000);
  const { settings, saveSettings, isLoading: _isSettingsLoading } = useSettings();
  const isMobile = useIsMobile();

  const isAdmin = user?.appRoles?.includes(AppRole.Admin) ?? false;
  // FORCED_ADMIN_EMAILS in AuthContext already promotes specific accounts to AppRole.Admin,
  // so a single role check is sufficient.
  const canSeeTestIncidentButton = isAdmin;

  // Play sound notification for new incident
  const playSoundNotification = useCallback((incident: Incident) => {
    const soundConfig = settings.soundConfigs.find(c => c.severity === incident.severity);
    if (soundConfig?.enabled && soundConfig.soundFileUrl) {
      try {
        const audio = new Audio(soundConfig.soundFileUrl);
        audio.play().catch(err => console.error('Failed to play sound:', err));
      } catch (err) {
        console.error('Failed to create audio:', err);
      }
    }
  }, [settings.soundConfigs]);

  // Handle new incident notification
  const handleNewIncident = useCallback((incident: Incident) => {
    // Play sound only in wall screen mode
    if (isWallScreen) {
      playSoundNotification(incident);
    } else {
      // Show toast only in personal mode
      const severityLabel = t(`statusPanel.severity.${incident.severity}`);
      showToast({
        title: t('statusPanel.toast.newIncident'),
        body: t('statusPanel.toast.incidentCreated', { severity: severityLabel, title: incident.title }),
        intent: getSeverityIntent(incident.severity),
        timeout: 8000,
      });
    }
  }, [t, showToast, isWallScreen, playSoundNotification]);

  const incidentOptions = useMemo(() => ({ onNewIncident: handleNewIncident }), [handleNewIncident]);
  const { incidents, closedLast24h, isLoading, error, connectionStatus, refresh } = useIncidents(incidentOptions);

  const handleSettingsSave = useCallback(async (newSettings: StatusPanelSettings) => {
    try {
      await saveSettings(newSettings);
    } catch (err) {
      showToast({
        title: t('statusPanel.settings.saveFailed'),
        body: t('statusPanel.settings.saveFailedDescription'),
        intent: 'error',
        timeout: 5000,
      });
    }
  }, [saveSettings, showToast, t]);

  // Create test incident (dev only)
  const handleCreateTestIncident = useCallback(async () => {
    try {
      await apiClient.createTestIncident('High');
      showToast({
        title: t('statusPanel.testIncident.createdTitle'),
        body: t('statusPanel.testIncident.createdBody'),
        intent: 'success',
        timeout: 3000,
      });
    } catch (err) {
      showToast({
        title: t('statusPanel.testIncident.failedTitle'),
        body: String(err),
        intent: 'error',
        timeout: 5000,
      });
    }
  }, [showToast, t]);

  // Toggle browser fullscreen when entering/exiting wall-screen mode
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Failed to enter fullscreen:', err);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error('Failed to exit fullscreen:', err);
      });
    }
  }, []);

  // Keyboard shortcut for toggling wall-screen mode (Escape to exit)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isWallScreen) {
        toggleMode();
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isWallScreen, toggleMode]);

  const handleModeToggle = () => {
    toggleMode();
    // Go fullscreen when entering wall-screen mode
    if (!isWallScreen) {
      toggleFullscreen();
    }
  };

  // Compute stats for wall-screen mode
  const stats = useMemo(() => {
    const active = incidents.filter((i) => i.state !== IncidentState.Closed);
    const nowMs = Date.now();
    return {
      active: active.length,
      high: active.filter((i) => i.severity === IncidentSeverity.High).length,
      overdue: active.filter((i) => new Date(i.slaDeadline).getTime() < nowMs).length,
      unassigned: active.filter((i) => !i.assignedOperatorId).length,
      closed: closedLast24h,
    };
  }, [incidents, closedLast24h]);

  // Wall-screen mode renders a completely different layout
  if (isWallScreen) {
    const timeStr = new Date(now).toLocaleTimeString('en-GB');
    const dateStr = new Date(now)
      .toLocaleDateString('en-GB', { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' })
      .toUpperCase();

    return (
      <div className={mergeClasses(styles.root, styles.wallScreenRoot)}>
        {/* Google Fonts for wall-screen mode */}
        <link
          href="https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Barlow+Condensed:wght@400;600;700;900&display=swap"
          rel="stylesheet"
        />

        {/* Header */}
        <div className={mergeClasses(styles.toolbar, styles.wallScreenToolbar)}>
          <div className={mergeClasses(styles.toolbarLeft, styles.wallScreenToolbarLeft)}>
            <span className={styles.wallScreenSubtitle}>{t('statusPanel.wallScreen.subtitle')}</span>
            <span className={styles.wallScreenTitle}>{t('statusPanel.wallScreen.queueTitle')}</span>
          </div>
          <div className={mergeClasses(styles.toolbarRight, styles.wallScreenToolbarRight)}>
            <LiveIndicator now={now.getTime()} staleThresholdHours={settings.staleThresholdHours} />
            <span className={styles.wallScreenDate}>{dateStr}</span>
            <span className={styles.wallScreenTime}>{timeStr}</span>
          </div>
        </div>

        {/* Stats Bar */}
        <div className={styles.statsBar}>
          <StatCard label={t('statusPanel.stats.active')} value={stats.active} color={SOC_COLORS.teal} styles={styles} />
          <StatCard label={t('statusPanel.stats.high')} value={stats.high} color={SOC_COLORS.red} styles={styles} />
          <StatCard label={t('statusPanel.stats.overdue')} value={stats.overdue} color={SOC_COLORS.red} styles={styles} />
          <StatCard label={t('statusPanel.stats.unassigned')} value={stats.unassigned} color={SOC_COLORS.yellow} styles={styles} />
          <StatCard label={t('statusPanel.stats.closed')} value={stats.closed} color={SOC_COLORS.green} styles={styles} />
        </div>

        {/* Unassigned Incidents Priority Queue */}
        <UnassignedIncidentsBar incidents={incidents} />

        {/* Incident List */}
        <div className={mergeClasses(styles.content, styles.wallScreenContent)}>
          <WallScreenIncidentList
            incidents={incidents}
            isLoading={isLoading}
            error={error}
          />
        </div>

        {/* Footer with exit hint */}
        <div className={styles.wallScreenFooter}>
          <span className={styles.wallScreenFooterText}>
            {t('statusPanel.pressEscToExit')} · {t('statusPanel.wallScreen.totalWithCount', { count: incidents.length })}
          </span>
          <Button
            appearance="subtle"
            size="small"
            onClick={handleModeToggle}
            className={styles.wallScreenExitButton}
          >
            {t('statusPanel.exitWallScreen')}
          </Button>
        </div>
      </div>
    );
  }

  // Personal mode - standard layout
  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Text size={500} weight="semibold">
            {t('statusPanel.title')}
          </Text>
          <Badge appearance="filled" color="informative">
            {incidents.length}
          </Badge>
        </div>
        <div className={styles.toolbarRight}>
          {isMobile ? (
            // Mobile: compact toolbar — dot indicator + settings + refresh + overflow "…"
            <>
              <ConnectionStatus status={connectionStatus} compact />
              <SettingsDialog
                settings={settings}
                onSave={handleSettingsSave}
                trigger={
                  <Button icon={<SettingsRegular />} appearance="subtle" size="small" />
                }
              />
              <Button
                icon={<ArrowClockwiseRegular />}
                appearance="subtle"
                onClick={refresh}
                size="small"
              />
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    icon={<MoreHorizontalRegular />}
                    appearance="subtle"
                    size="small"
                    aria-label={t('statusPanel.moreActions')}
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem icon={<PersonRegular />} onClick={handleModeToggle}>
                      {t('statusPanel.wallScreenMode')}
                    </MenuItem>
                    {canSeeTestIncidentButton && (
                      <MenuItem icon={<BugRegular />} onClick={handleCreateTestIncident}>
                        {t('statusPanel.testIncident.buttonLabel')}
                      </MenuItem>
                    )}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </>
          ) : (
            // Desktop: full toolbar
            <>
              <ConnectionStatus status={connectionStatus} />
              {canSeeTestIncidentButton && (
                <Button
                  icon={<BugRegular />}
                  appearance="subtle"
                  size="small"
                  onClick={handleCreateTestIncident}
                  title={t('statusPanel.testIncident.buttonTooltip')}
                >
                  {t('statusPanel.testIncident.buttonLabel')}
                </Button>
              )}
              <SettingsDialog
                settings={settings}
                onSave={handleSettingsSave}
                trigger={
                  <Button icon={<SettingsRegular />} appearance="subtle" size="small">
                    {t('statusPanel.settings.title')}
                  </Button>
                }
              />
              <ToggleButton
                checked={false}
                icon={<PersonRegular />}
                onClick={handleModeToggle}
                size="small"
              >
                {t('statusPanel.personalMode')}
              </ToggleButton>
              <Button
                icon={<ArrowClockwiseRegular />}
                appearance="subtle"
                onClick={refresh}
                size="small"
              />
            </>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <IncidentTable
          incidents={incidents}
          isLoading={isLoading}
          error={error}
          onRefresh={refresh}
          settings={settings}
          isWallScreen={false}
        />
      </div>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  styles: ReturnType<typeof useStatusPanelPageStyles>;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, styles }) => (
  <div className={styles.statCard} style={{ borderLeftColor: color }}>
    <div className={styles.statValue} style={{ color, textShadow: `0 0 18px ${color}44` }}>
      {value}
    </div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);
